import axios from 'axios';
import { Organization } from './models/Organization';
import { UserProfile } from './models/Profile';
import { Page } from './models/HostRoomBooking';
import { GrowthSummary } from './models/Growth';
import { CampaignUser, Campaign, CreateCampaignRequest } from './models/Campaign';
import { Space } from './models/Space';
import { SpaceRankBoost, RemoveSpaceRankBoostRequest } from './models/SpaceRankBoost';
import { CreateFakeUserRequest, FakeUserInfo, AdminCreateSpaceReviewRequest, CreateResponse } from './models/FakeUser';
import { AdminIndividualTutorUser } from './models/AdminIndividualTutorUser';
import { RefundRoomBookingRequest } from './models/RefundRoomBookingRequest';
import {
  AdminAuditLogPage,
  AdminAuditLogQuery,
  buildAdminAuditLogParams,
} from './models/AdminAuditLog';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';
import {
  CreateOrganizationSubscriptionPlanOfferRequest,
  OrganizationSubscriptionPlanOffer,
  OrganizationSubscriptionPlanSubscriber,
  SubscriptionPlanSummary,
} from './models/Subscription';

// Define the base API URL based on the environment
const baseURL = process.env.REACT_APP_API_BASE_URL + "/admin";

class AdminApiClient {
  private axiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem("access_token");
    
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        applyClientChannelHeader(config.headers);
        applyAllowTestOrgsHeader(config.headers);
    
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => this.handleErrorResponse(error)
    );
  }

  private handleErrorResponse(error: any): Promise<never> {
    const status = error.response ? error.response.status : null;

    if (status === 401) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("phone_number");
    }

    return Promise.reject(error);
  }

  public async getOrganizations(): Promise<Organization[]> {
    try {
      const response = await this.axiosInstance.get<Organization[]>('/organizations');
      return response.data;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
  }

  public async enableOrganizationExperimentalFeatures(organizationId: string): Promise<void> {
    await this.axiosInstance.post<void>(
      `/organizations/${organizationId}/experimental-features/enable`,
      {}
    );
  }

  public async disableOrganizationExperimentalFeatures(organizationId: string): Promise<void> {
    await this.axiosInstance.post<void>(
      `/organizations/${organizationId}/experimental-features/disable`,
      {}
    );
  }

  public async updateOrganizationEnabledStatus(organizationId: string, enabled: boolean): Promise<void> {
    try {
      await this.axiosInstance.put<void>(`/organizations/${organizationId}/enabled`, {
        enabled
      });
    } catch (error) {
      console.error(`Error updating organization enabled status for ${organizationId}:`, error);
      throw error;
    }
  }

  public async updateOrganizationTestStatus(organizationId: string, test: boolean): Promise<void> {
    await this.axiosInstance.put<void>(`/organizations/${organizationId}/test`, {
      test
    });
  }

  /** GET /v1/admin/subscription-plans/private */
  public async getPrivateSubscriptionPlans(): Promise<SubscriptionPlanSummary[]> {
    const response = await this.axiosInstance.get<SubscriptionPlanSummary[]>(
      '/subscription-plans/private',
    );
    return response.data;
  }

  /** GET /v1/admin/subscription-plans/self-service */
  public async getSelfServiceSubscriptionPlans(): Promise<SubscriptionPlanSummary[]> {
    const response = await this.axiosInstance.get<SubscriptionPlanSummary[]>(
      '/subscription-plans/self-service',
    );
    return response.data;
  }

  /** GET /v1/admin/subscription-plans/{subscriptionPlanPriceId}/offers */
  public async listSubscriptionPlanOffersByPriceId(
    subscriptionPlanPriceId: string,
  ): Promise<OrganizationSubscriptionPlanOffer[]> {
    const response = await this.axiosInstance.get<OrganizationSubscriptionPlanOffer[]>(
      `/subscription-plans/${subscriptionPlanPriceId}/offers`,
    );
    return response.data;
  }

  /** GET /v1/admin/subscription-plans/{subscriptionPlanPriceId}/subscribers */
  public async listSubscriptionPlanSubscribersByPriceId(
    subscriptionPlanPriceId: string,
  ): Promise<OrganizationSubscriptionPlanSubscriber[]> {
    const response = await this.axiosInstance.get<OrganizationSubscriptionPlanSubscriber[]>(
      `/subscription-plans/${subscriptionPlanPriceId}/subscribers`,
    );
    return response.data;
  }

  /** GET /v1/admin/organizations/{organizationId}/subscription-plan-offers */
  public async listSubscriptionPlanOffers(
    organizationId: string,
  ): Promise<OrganizationSubscriptionPlanOffer[]> {
    const response = await this.axiosInstance.get<OrganizationSubscriptionPlanOffer[]>(
      `/organizations/${organizationId}/subscription-plan-offers`,
    );
    return response.data;
  }

  /** POST /v1/admin/organizations/{organizationId}/subscription-plan-offers */
  public async grantSubscriptionPlanOffer(
    organizationId: string,
    request: CreateOrganizationSubscriptionPlanOfferRequest,
  ): Promise<void> {
    await this.axiosInstance.post<void>(
      `/organizations/${organizationId}/subscription-plan-offers`,
      request,
    );
  }

  /** DELETE /v1/admin/organizations/{organizationId}/subscription-plan-offers/{subscriptionPlanPriceId} */
  public async revokeSubscriptionPlanOffer(
    organizationId: string,
    subscriptionPlanPriceId: string,
  ): Promise<void> {
    await this.axiosInstance.delete<void>(
      `/organizations/${organizationId}/subscription-plan-offers/${subscriptionPlanPriceId}`,
    );
  }

  public async getUsers(page: number = 0, size: number = 10): Promise<Page<UserProfile>> {
    try {
      const response = await this.axiosInstance.get<Page<UserProfile>>('/users', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  public async getIndividualTutorUsers(page: number = 0, size: number = 10): Promise<Page<AdminIndividualTutorUser>> {
    try {
      const response = await this.axiosInstance.get<Page<AdminIndividualTutorUser>>('/users/individual-tutors', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching individual tutor users:', error);
      throw error;
    }
  }

  public async getHostUsers(page: number = 0, size: number = 10): Promise<Page<AdminIndividualTutorUser>> {
    try {
      const response = await this.axiosInstance.get<Page<AdminIndividualTutorUser>>('/users/hosts', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching host users:', error);
      throw error;
    }
  }

  public async getTutorUsers(page: number = 0, size: number = 10): Promise<Page<AdminIndividualTutorUser>> {
    try {
      const response = await this.axiosInstance.get<Page<AdminIndividualTutorUser>>('/users/tutors', {
        params: { page, size }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tutor users:', error);
      throw error;
    }
  }

  public async getGrowth(): Promise<GrowthSummary> {
    try {
      const response = await this.axiosInstance.get<GrowthSummary>('/growth');
      return response.data;
    } catch (error) {
      console.error('Error fetching growth data:', error);
      throw error;
    }
  }

  public async getCampaignUsers(): Promise<CampaignUser[]> {
    try {
      const response = await this.axiosInstance.get<CampaignUser[]>('/campaigns/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign users:', error);
      throw error;
    }
  }

  public async getCampaignCountries(): Promise<string[]> {
    try {
      const response = await this.axiosInstance.get<string[]>('/campaigns/countries');
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign countries:', error);
      throw error;
    }
  }

  public async createCampaign(request: CreateCampaignRequest): Promise<void> {
    try {
      await this.axiosInstance.post<void>('/campaigns', request);
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  public async triggerSend(campaignId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/campaigns/${campaignId}/trigger-send`);
    } catch (error) {
      console.error(`Error triggering send for campaign ${campaignId}:`, error);
      throw error;
    }
  }

  public async triggerRetryFailed(campaignId: string): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/campaigns/${campaignId}/trigger-retry-failed`);
    } catch (error) {
      console.error(`Error triggering retry failed for campaign ${campaignId}:`, error);
      throw error;
    }
  }

  public async getAllCampaigns(): Promise<Campaign[]> {
    try {
      const response = await this.axiosInstance.get<Campaign[]>('/campaigns');
      return response.data;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  }

  public async getAllSpaces(): Promise<Space[]> {
    try {
      const response = await this.axiosInstance.get<Space[]>('/spaces');
      return response.data;
    } catch (error) {
      console.error('Error fetching spaces:', error);
      throw error;
    }
  }

  public async addSpaceRankBoost(spaceId: string, boost: SpaceRankBoost): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/spaces/${spaceId}/boosts`, boost);
    } catch (error) {
      console.error(`Error adding space rank boost for ${spaceId}:`, error);
      throw error;
    }
  }

  public async removeSpaceRankBoost(spaceId: string, request: RemoveSpaceRankBoostRequest): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/spaces/${spaceId}/boosts`, {
        data: request
      });
    } catch (error) {
      console.error(`Error removing space rank boost for ${spaceId}:`, error);
      throw error;
    }
  }

  public async createFakeUser(request: CreateFakeUserRequest): Promise<CreateResponse> {
    try {
      const response = await this.axiosInstance.post<CreateResponse>('/users/fake', request);
      return response.data;
    } catch (error) {
      console.error('Error creating fake user:', error);
      throw error;
    }
  }

  public async listFakeUsers(): Promise<FakeUserInfo[]> {
    try {
      const response = await this.axiosInstance.get<FakeUserInfo[]>('/users/fake');
      return response.data;
    } catch (error) {
      console.error('Error listing fake users:', error);
      throw error;
    }
  }

  public async deleteFakeUser(userId: string): Promise<void> {
    try {
      await this.axiosInstance.delete<void>(`/users/fake/${userId}`);
    } catch (error) {
      console.error(`Error deleting fake user ${userId}:`, error);
      throw error;
    }
  }

  public async createReviewOnBehalf(spaceId: string, request: AdminCreateSpaceReviewRequest): Promise<CreateResponse> {
    try {
      const response = await this.axiosInstance.post<CreateResponse>(`/spaces/${spaceId}/reviews`, request);
      return response.data;
    } catch (error) {
      console.error(`Error creating review on behalf for space ${spaceId}:`, error);
      throw error;
    }
  }

  /** GET /v1/admin/audit-logs */
  public async getAuditLogs(query: AdminAuditLogQuery = {}): Promise<AdminAuditLogPage> {
    try {
      const response = await this.axiosInstance.get<AdminAuditLogPage>('/audit-logs', {
        params: buildAdminAuditLogParams(query),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching admin audit logs:', error);
      throw error;
    }
  }

  /** POST /v1/admin/room-bookings/{bookingId}/refund */
  public async refundRoomBooking(bookingId: string, request: RefundRoomBookingRequest = {}): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/room-bookings/${bookingId}/refund`, request);
    } catch (error) {
      console.error(`Error refunding room booking ${bookingId}:`, error);
      throw error;
    }
  }

  /** POST /v1/admin/tutor-bookings/{bookingId}/refund */
  public async refundTutorBooking(bookingId: string, request: RefundRoomBookingRequest = {}): Promise<void> {
    try {
      await this.axiosInstance.post<void>(`/tutor-bookings/${bookingId}/refund`, request);
    } catch (error) {
      console.error(`Error refunding tutor booking ${bookingId}:`, error);
      throw error;
    }
  }

}

export default AdminApiClient;

export const adminApiClient = new AdminApiClient(); 